import React, { useState, useEffect } from "react";
import { useFetchVendorRevendorizations } from "./hooks/useFetchVendorRevendorizations";
import { useCreateVendorRules } from "./hooks/useCreateVendorRules";
import {
  Box,
  CircularProgress,
  Button,
  Link
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer
} from "@mui/x-data-grid";
import { useSearchParams } from "react-router-dom";

const columns = [
  {
    field: "originalVendorName",
    headerName: "Original Vendor",
    width: 250,
    flex: 1,
    renderCell: (params) => (
      <Link
        href={`/vendors/${params.row.originalVendorId}`}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        onClick={(e) => e.stopPropagation()}
      >
        {params.value}
      </Link>
    )
  },
  {
    field: "remappedVendorName",
    headerName: "Remapped Vendor",
    width: 250,
    flex: 1,
    renderCell: (params) => (
      <Link
        href={`/vendors/${params.row.newVendorId}`}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        onClick={(e) => e.stopPropagation()}
      >
        {params.value}
      </Link>
    )
  },
  {
    field: "remappingCount",
    headerName: "User Remappings",
    width: 120,
    type: "number"
  }
];

const Toolbar = ({ onCreateRuleClick, disabled }) => {
  return (
    <GridToolbarContainer sx={{ mb: 1 }}>
      <Box sx={{ flexGrow: 1 }} />
      <Button
        variant="contained"
        color="primary"
        onClick={onCreateRuleClick}
        disabled={disabled}
      >
        Create Rule
      </Button>
    </GridToolbarContainer>
  );
};

export const VendorRevendorizationList = () => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { createRules } = useCreateVendorRules();

  const [paginationModel, setPaginationModel] = useState({
    page: parseInt(searchParams.get("page") ?? 0, 10),
    pageSize: parseInt(searchParams.get("pageSize") ?? 25, 10)
  });

  const { data, isLoading, refetch } = useFetchVendorRevendorizations({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize
  });

  useEffect(() => {
    setSearchParams({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize
    });
  }, [paginationModel, setSearchParams]);

  // Add unique ID to each row for DataGrid
  const rowsWithId = data?.revendorizations?.map((row) => ({
    ...row,
    id: `${row.originalVendorId}-${row.newVendorId}`
  })) ?? [];

  const handleCreateRule = async () => {
    const rules = selectedRows.map((rowId) => {
      const row = rowsWithId.find((r) => r.id === rowId);

      // Validate that values are purely numeric before parsing
      if (!/^\d+$/.test(row.originalVendorId) ||
        !/^\d+$/.test(row.newVendorId)) {
        throw new Error("Invalid vendor ID format");
      }

      const originalVendorId = parseInt(row.originalVendorId, 10);
      const newVendorId = parseInt(row.newVendorId, 10);

      return {
        originalVendorId,
        newVendorId
      };
    });

    try {
      await createRules.mutateAsync({ rules });
      setSelectedRows([]);
      refetch();
    } catch (error) {
      // Error handling would go here
    }
  };

  if (isLoading) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="32px">
      <DataGrid
        rows={rowsWithId}
        columns={columns}
        rowCount={data?.total}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        checkboxSelection
        onRowSelectionModelChange={(newValue) => {
          setSelectedRows(newValue);
        }}
        rowSelectionModel={selectedRows}
        disableRowSelectionOnClick
        loading={isLoading}
        slots={{ toolbar: Toolbar }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton"
          },
          toolbar: {
            onCreateRuleClick: selectedRows.length > 0
              ? handleCreateRule
              : null,
            disabled: selectedRows.length === 0
          }
        }}
      />
    </Box>
  );
};
